import React, {Fragment, useEffect, useState, useRef} from "react";
import Breadcrumb from "../../../layout/breadcrumb";
import {Button, Card, CardBody, CardHeader, Col, Container, Row, Badge} from "reactstrap";
import CommonDataTable from "../../../share-components/table/commonDataTable";
import {useDispatch, useSelector} from "react-redux";
import {
    getSmartReportSettings,
    getSmartReportModuleColumns,
    getSmartReportHistory,
    generateSmartReport,
    getSmartReportById,
    deleteSmartReport,
    selectSmartReports,
    setGenerating,
    clearModuleColumns,
} from "./store/smartReportSlice";
import withReducer from "../../../store/withReducer";
import reportReducer from "../index";
import Select from "react-select";
import moment from "moment";
import DatePicker from "../../../share-components/ui/DateRangePicker";
import {DateRangePicker} from "rsuite";
import PreLoader from "../../../share-components/ui/preLoader";
import SweetAlert from "sweetalert2";
import {toast} from "react-toastify";
import ColumnPicker from "./columnPicker";
import SmartReportPreview from "./reportPreview";
import SmartReportFilter from "./smartReportFilter";

const {afterToday} = DateRangePicker;

const customStyles = {
    control: (base) => ({
        ...base,
        minHeight: 50,
        border: "none",
        backgroundColor: 'rgba(211, 211, 211, 0.11)',
        borderRadius: 12
    }),
};

const dataMenu = [
    {type: 0, text: 'Smart Reports'},
];

const statusBadge = (status) => {
    const map = {
        'pending': {color: 'warning', text: 'Pending'},
        'processing': {color: 'info', text: 'Processing'},
        'completed': {color: 'success', text: 'Completed'},
        'failed': {color: 'danger', text: 'Failed'},
    };
    const s = map[status] || {color: 'secondary', text: status};
    return <Badge color={s.color}>{s.text}</Badge>;
};

const tableColumns = [
    {
        name: "Report Name",
        selector: (row) => row.report_name || 'Generating...',
        sortable: true,
        left: true,
        minWidth: '200px',
    },
    {
        name: "Module",
        selector: (row) => row.module,
        sortable: true,
        center: true,
        cell: (row) => <Badge color="light" style={{textTransform: 'capitalize'}}>{row.module}</Badge>,
    },
    {
        name: "Status",
        selector: (row) => row.status,
        sortable: true,
        center: true,
        cell: (row) => statusBadge(row.status),
    },
    {
        name: "Rows",
        selector: (row) => row.row_count != null ? row.row_count : '-',
        sortable: true,
        center: true,
    },
    {
        name: "Time",
        selector: (row) => row.execution_time_ms ? (row.execution_time_ms / 1000).toFixed(1) + 's' : '-',
        sortable: true,
        center: true,
    },
    {
        name: "Date",
        selector: (row) => moment(row.created_at).format('DD/MM/YYYY HH:mm'),
        sortable: true,
        center: true,
    },
    {
        name: "By",
        selector: (row) => row.generated_by || '-',
        sortable: false,
        center: true,
    },
];

const SmartReport = () => {
    const dispatch = useDispatch();

    const TodayDate = new Date();
    const thirtyDaysAgo = new Date(new Date().setDate(TodayDate.getDate() - 30));
    const [dateRange, setDateRange] = useState([thirtyDaysAgo, TodayDate]);
    const [startDate, endDate] = dateRange;

    const [selectedModule, setSelectedModule] = useState(null);
    const [seller, setSeller] = useState(null);
    const [partner, setPartner] = useState(null);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [extraFilters, setExtraFilters] = useState({});
    const [searchText, setSearchText] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [loading, setLoading] = useState(false);
    const [columnsLoading, setColumnsLoading] = useState(false);
    const [generateBtnDisabled, setGenerateBtnDisabled] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [selectedReport, setSelectedReport] = useState(null);

    const pollingRef = useRef(null);

    const smartReportState = useSelector(({report}) => report.smartReport);
    const smartReports = useSelector(selectSmartReports);

    useEffect(() => {
        dispatch(getSmartReportSettings());
    }, [dispatch]);

    useEffect(() => {
        loadHistory();
    }, [page, pageSize, dispatch]);

    useEffect(() => {
        return () => {
            if (pollingRef.current) {
                clearInterval(pollingRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedModule) {
            setColumnsLoading(true);
            setSelectedColumns([]);
            setExtraFilters({});
            setSearchText('');
            setSeller(null);
            setPartner(null);
            dispatch(getSmartReportModuleColumns(selectedModule.key)).then((res) => {
                setColumnsLoading(false);
                if (!res.error && res.payload && res.payload.default_columns) {
                    setSelectedColumns(res.payload.default_columns);
                }
            });
        } else {
            dispatch(clearModuleColumns());
            setSelectedColumns([]);
        }
    }, [selectedModule, dispatch]);

    const loadHistory = () => {
        setLoading(true);
        dispatch(getSmartReportHistory({page, pageSize})).then(() => {
            setLoading(false);
        });
    };

    const startPolling = (reportId) => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
        }
        pollingRef.current = setInterval(() => {
            dispatch(getSmartReportById(reportId)).then((res) => {
                if (!res.error && res.payload) {
                    const status = res.payload.status;
                    if (status === 'completed' || status === 'failed') {
                        clearInterval(pollingRef.current);
                        pollingRef.current = null;
                        dispatch(setGenerating(false));
                        loadHistory();
                        if (status === 'completed') {
                            toast.success('Report generated successfully!', {position: toast.POSITION.TOP_RIGHT});
                        } else {
                            toast.error(res.payload.error_message || 'Report generation failed.', {position: toast.POSITION.TOP_RIGHT});
                        }
                    }
                }
            });
        }, 3000);
    };

    const handleGenerate = () => {
        if (!selectedModule) {
            toast.error('Please select a report module.', {position: toast.POSITION.TOP_RIGHT});
            return;
        }
        if (selectedColumns.length === 0) {
            toast.error('Please select at least one column.', {position: toast.POSITION.TOP_RIGHT});
            return;
        }
        if (!startDate || !endDate) {
            toast.error('Please select a date range.', {position: toast.POSITION.TOP_RIGHT});
            return;
        }

        setGenerateBtnDisabled(true);

        const data = {
            module: selectedModule.key,
            start_date: moment(startDate).format('YYYY-MM-DD'),
            end_date: moment(endDate).format('YYYY-MM-DD'),
            selected_columns: selectedColumns,
            seller_id: seller ? seller.id : null,
            partner_id: partner ? partner.id : null,
            extra_filters: extraFilters,
            search: searchText || null,
        };

        dispatch(generateSmartReport(data)).then((res) => {
            setGenerateBtnDisabled(false);
            if (!res.error && res.payload && res.payload.id) {
                toast.info('Report generation started...', {position: toast.POSITION.TOP_RIGHT});
                startPolling(res.payload.id);
            }
        });
    };

    const handleToggleColumn = (key) => {
        setSelectedColumns(prev => {
            if (prev.includes(key)) {
                return prev.filter(c => c !== key);
            }
            return [...prev, key];
        });
    };

    const handleSelectAll = () => {
        if (smartReportState.columnGroups && smartReportState.columnGroups.length > 0) {
            const allKeys = [];
            smartReportState.columnGroups.forEach(g => {
                g.columns.forEach(c => allKeys.push(c.key));
            });
            setSelectedColumns(allKeys);
        }
    };

    const handleDeselectAll = () => {
        setSelectedColumns([]);
    };

    const handleReorder = (newOrder) => {
        setSelectedColumns(newOrder);
    };

    const handleView = (row) => {
        if (row.status === 'completed' || row.status === 'failed') {
            setSelectedReport(row);
            setPreviewOpen(true);
        } else {
            toast.info('Report is still ' + row.status + '.', {position: toast.POSITION.TOP_RIGHT});
        }
    };

    const handleDownload = (row) => {
        if (row.file_url) {
            const link = document.createElement("a");
            link.href = row.file_url;
            link.setAttribute("download", "");
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    };

    const handleDelete = (row) => {
        SweetAlert.fire({
            title: 'Are you sure?',
            text: "This will permanently delete the report and its file.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No, cancel',
            reverseButtons: true
        }).then((result) => {
            if (result.value) {
                dispatch(deleteSmartReport(row.id)).then(() => {
                    toast.success('Report deleted.', {position: toast.POSITION.TOP_RIGHT});
                });
            }
        });
    };

    const pagination = (pageSize) => {
        setPageSize(pageSize);
    };

    const actionColumns = [
        ...tableColumns,
        {
            name: 'Action',
            selector: row => (
                <>
                    <a className="cursor-pointer mr-2" onClick={() => handleView(row)} title="Preview">
                        <i className="fa fa-eye"></i>
                    </a>
                    {row.status === 'completed' && row.file_url && (
                        <a className="cursor-pointer mr-2" onClick={() => handleDownload(row)} title="Download">
                            <i className="fa fa-download"></i>
                        </a>
                    )}
                    <a className="cursor-pointer" onClick={() => handleDelete(row)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </a>
                </>
            ),
            sortable: false,
            center: true,
            minWidth: '120px',
        }
    ];

    return (
        <Fragment>
            <Breadcrumb title="Smart Reports" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <div className="product-grid">
                    <div className="feature-products">
                        <Card className="mb-3">
                            <CardBody className="p-3">
                                <Row className="align-items-center mb-3">
                                    <Col md="">
                                        <div className="dashboard-datepicker">
                                            <DatePicker
                                                dateRange={dateRange}
                                                dateRangeChange={(date) => setDateRange(date)}
                                                defaultValue={[thirtyDaysAgo, TodayDate]}
                                                disabledDate={afterToday()}
                                                paddingLeft={0}
                                            />
                                        </div>
                                    </Col>
                                    <Col md="">
                                        <Select
                                            placeholder="Select Report Type"
                                            className="select2-filter"
                                            menuPortalTarget={document.body}
                                            getOptionLabel={(option) => option.label}
                                            getOptionValue={(option) => option.key}
                                            isClearable
                                            styles={{...customStyles, menuPortal: base => ({...base, zIndex: 100})}}
                                            components={{IndicatorSeparator: () => null}}
                                            options={smartReportState.settings.modules}
                                            onChange={(e) => setSelectedModule(e ? e : null)}
                                        />
                                    </Col>
                                    <Col md="">
                                        <Select
                                            placeholder="Select Seller"
                                            className="select2-filter"
                                            menuPortalTarget={document.body}
                                            getOptionLabel={(option) => option.shop_name}
                                            getOptionValue={(option) => option.id}
                                            isClearable
                                            value={seller}
                                            styles={{...customStyles, menuPortal: base => ({...base, zIndex: 100})}}
                                            components={{IndicatorSeparator: () => null}}
                                            options={
                                                smartReportState.onlySellerIds.length > 0
                                                    ? smartReportState.settings.sellers.filter(s => smartReportState.onlySellerIds.includes(s.id))
                                                    : smartReportState.settings.sellers
                                            }
                                            onChange={(e) => setSeller(e ? e : null)}
                                        />
                                    </Col>
                                    <Col md="">
                                        <Select
                                            placeholder="Select Partner"
                                            className="select2-filter"
                                            menuPortalTarget={document.body}
                                            getOptionLabel={(option) => option.name}
                                            getOptionValue={(option) => option.id}
                                            isClearable
                                            value={partner}
                                            styles={{...customStyles, menuPortal: base => ({...base, zIndex: 100})}}
                                            components={{IndicatorSeparator: () => null}}
                                            options={
                                                smartReportState.onlyPartnerIds.length > 0
                                                    ? smartReportState.settings.partners.filter(p => smartReportState.onlyPartnerIds.includes(p.id))
                                                    : smartReportState.excludePartnerIds.length > 0
                                                        ? smartReportState.settings.partners.filter(p => !smartReportState.excludePartnerIds.includes(p.id))
                                                        : smartReportState.settings.partners
                                            }
                                            onChange={(e) => setPartner(e ? e : null)}
                                        />
                                    </Col>
                                    {!columnsLoading && selectedModule && smartReportState.moduleFilters && smartReportState.moduleFilters.length > 0 && (
                                        <Col md="auto" className="d-flex align-items-center">
                                            <div
                                                style={{
                                                    border: '1px solid #dee2e6',
                                                    borderRadius: '8px',
                                                    padding: '12px 20px',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                    fontSize: '15px',
                                                    fontWeight: 600,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                }}
                                                onClick={() => {
                                                    document.querySelector(".customizer-contain").classList.add("open");
                                                }}
                                            >
                                                Filters
                                                {Object.values(extraFilters).filter(v => v !== null && v !== undefined).length > 0 && (
                                                    <Badge color="primary">
                                                        {Object.values(extraFilters).filter(v => v !== null && v !== undefined).length}
                                                    </Badge>
                                                )}
                                                <i className="fa fa-chevron-right" style={{fontSize: '12px'}}></i>
                                            </div>
                                        </Col>
                                    )}
                                </Row>

                                {!columnsLoading && selectedModule && smartReportState.searchPlaceholder && (
                                    <Row className="mb-3">
                                        <Col>
                                            <div style={{position: 'relative'}}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={smartReportState.searchPlaceholder}
                                                    value={searchText}
                                                    onChange={(e) => setSearchText(e.target.value)}
                                                    style={{
                                                        minHeight: 50,
                                                        border: '1px solid #dee2e6',
                                                        borderRadius: 12,
                                                        paddingRight: 40,
                                                        backgroundColor: 'rgba(211, 211, 211, 0.11)',
                                                    }}
                                                />
                                                <i
                                                    className="fa fa-search"
                                                    style={{
                                                        position: 'absolute',
                                                        right: 15,
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        color: '#999',
                                                    }}
                                                ></i>
                                            </div>
                                        </Col>
                                    </Row>
                                )}

                                {columnsLoading && (
                                    <div style={{minHeight: 200, position: 'relative'}}>
                                        <PreLoader/>
                                    </div>
                                )}

                                {!columnsLoading && smartReportState.columnGroups && smartReportState.columnGroups.length > 0 && (
                                    <ColumnPicker
                                        columnGroups={smartReportState.columnGroups}
                                        selectedColumns={selectedColumns}
                                        onToggle={handleToggleColumn}
                                        onSelectAll={handleSelectAll}
                                        onDeselectAll={handleDeselectAll}
                                        onReorder={handleReorder}
                                    />
                                )}

                                {selectedModule && !columnsLoading && (
                                    <Row>
                                        <Col className="d-flex align-items-center justify-content-between">
                                            <small className="text-muted">
                                                {selectedColumns.length} column{selectedColumns.length !== 1 ? 's' : ''} selected
                                            </small>
                                            <Button
                                                color="primary"
                                                onClick={handleGenerate}
                                                disabled={generateBtnDisabled || smartReportState.generating || selectedColumns.length === 0}
                                            >
                                                <i className={(generateBtnDisabled || smartReportState.generating) ? "fa fa-spinner fa-spin mr-1" : "fa fa-file-excel-o mr-1"}></i>
                                                {smartReportState.generating ? 'Generating...' : 'Generate Report'}
                                            </Button>
                                        </Col>
                                    </Row>
                                )}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardBody>
                                <h6 className="mb-3">Report History</h6>
                                {loading ? (
                                    <div style={{minHeight: 150}}>
                                        <PreLoader/>
                                    </div>
                                ) : (
                                    <div className="table-responsive product-table">
                                        <CommonDataTable
                                            headerColumns={actionColumns}
                                            gridData={smartReports}
                                            noAction
                                            paginationServer
                                            paginationTotalRows={smartReportState.tableRowCount}
                                            onChangePage={(page) => setPage(page)}
                                            onChangeRowsPerPage={pagination}
                                        />
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                </div>
                {selectedModule && smartReportState.moduleFilters && smartReportState.moduleFilters.length > 0 && (
                    <Row>
                        <div className="customizer-contain">
                            <div className="customizer-header">
                                <i
                                    className="icon-close"
                                    onClick={() => {
                                        document.querySelector(".customizer-contain").classList.remove("open");
                                    }}
                                ></i>
                                <h5>
                                    {"Filters      "}
                                    <a className="a-text-color ml-2">
                                        <i
                                            className="fa fa-refresh"
                                            title="Clear filters"
                                            onClick={() => setExtraFilters({})}
                                        ></i>
                                    </a>
                                </h5>
                                <SmartReportFilter
                                    moduleFilters={smartReportState.moduleFilters}
                                    settings={smartReportState.settings}
                                    extraFilters={extraFilters}
                                    setExtraFilters={setExtraFilters}
                                    onClearFilters={() => setExtraFilters({})}
                                />
                            </div>
                        </div>
                    </Row>
                )}
            </Container>

            <SmartReportPreview
                isOpen={previewOpen}
                toggle={() => setPreviewOpen(false)}
                report={selectedReport}
            />
        </Fragment>
    );
};

export default withReducer([{report: reportReducer}])(SmartReport);
